import {
  useMutation as useApolloMutation,
  useQuery as useApolloQuery,
} from '@apollo/client/react';
import { useMutation as useReactQueryMutation } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  strippingEquipmentsOptions,
  strippingFertilizationsOptions,
  updateFertilization,
} from '@/services/stripping';
import {
  EquipmentResponse,
  StrippingFertilizationResponse,
  UpdateFertilizationRequest,
} from '@/services/stripping/types';
import {
  Equipment,
  STRIPPING_FERTILIZATONS,
  STRIPPING_TECHNICIANS,
  STRIPPING_UPDATE_FERTILIZATION,
} from '@/types.gql';

const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useStrippingFertilizations({
  fertilizationDate,
  fertilizationSiteId,
  strippingTechnicianId,
}: {
  fertilizationDate: string;
  fertilizationSiteId: string | null;
  strippingTechnicianId: string | null;
}) {
  const isRestEnabled = useFeatureFlag('29181_stripping_graphql_to_rest');
  const hasRequiredParams =
    !!fertilizationDate && !!fertilizationSiteId && !!strippingTechnicianId;

  const { data: gqlData, refetch: refetchGql } = useApolloQuery(
    STRIPPING_FERTILIZATONS,
    {
      skip: isRestEnabled || !hasRequiredParams,
      variables: {
        date: fertilizationDate,
        siteId: fertilizationSiteId!,
        timeZone: TIME_ZONE,
      },
      fetchPolicy: 'network-only',
    },
  );

  const { data: restData, refetch: refetchRest } = useQuery({
    ...strippingFertilizationsOptions({ fertilizationDate }),
    enabled: isRestEnabled && hasRequiredParams,
  });

  const data: StrippingFertilizationResponse[] | undefined = isRestEnabled
    ? (restData as StrippingFertilizationResponse[] | undefined)
    : (gqlData?.strippingFertilizations?.items as
        | StrippingFertilizationResponse[]
        | undefined);

  const refetch = isRestEnabled ? refetchRest : refetchGql;

  return { data, refetch };
}

export function useUpdateFertilization() {
  const isRestEnabled = useFeatureFlag('29181_stripping_graphql_to_rest');

  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(
    STRIPPING_UPDATE_FERTILIZATION,
  );
  const { mutateAsync: restMutate, isPending: restLoading } =
    useReactQueryMutation({
      mutationFn: ({
        id,
        input,
      }: {
        id: string;
        input: UpdateFertilizationRequest;
      }) => updateFertilization(id, input),
    });

  const isPending = isRestEnabled ? restLoading : gqlLoading;

  const mutate = async ({
    id,
    input,
    timeZone,
  }: {
    id: string;
    input: Omit<UpdateFertilizationRequest, 'ianaTimeZone'>;
    timeZone: string;
  }) => {
    if (isRestEnabled) {
      await restMutate({ id, input: { ...input, ianaTimeZone: timeZone } });
    } else {
      await gqlMutate({ variables: { id, input, timeZone } });
    }
  };

  return { mutate, isPending };
}

export function useStrippingTechnicians({
  siteId,
  isTravelingTechnician,
}: {
  siteId: string | null;
  isTravelingTechnician: boolean;
}): EquipmentResponse[] | undefined {
  const isRestForStrippingEnabled = useFeatureFlag(
    '29181_stripping_graphql_to_rest',
  );

  const { data: gqlData } = useApolloQuery(STRIPPING_TECHNICIANS, {
    skip: isRestForStrippingEnabled,
    variables: {
      where: isTravelingTechnician ? {} : { siteId: { eq: siteId } },
    },
  });

  const { data: restData } = useQuery({
    ...strippingEquipmentsOptions(
      !isTravelingTechnician && siteId ? { siteId } : {},
    ),
    enabled: isRestForStrippingEnabled,
  });

  return isRestForStrippingEnabled
    ? (restData as EquipmentResponse[] | undefined)
    : (gqlData?.currentEquipments?.items as Equipment[] | undefined);
}
